import * as React from "react";
import { theme } from "@/theme/theme";
import { cn } from "@/lib/utils";

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ children, header, footer, className, ...props }, ref) => {
    const cardStyles: React.CSSProperties = {
      backgroundColor: theme.colors.white,
      border: `1px solid ${theme.colors.gray[300]}`,
      borderRadius: theme.radius.md,
      boxShadow: theme.shadows.card,
      padding: theme.spacing.md,
      display: "flex",
      flexDirection: "column",
    };

    return (
      <div ref={ref} style={cardStyles} className={cn(className)} {...props}>
        {header && (
          <div
            style={{
              marginBottom: theme.spacing.md,
              paddingBottom: theme.spacing.sm,
              borderBottom: `1px solid ${theme.colors.gray[100]}`,
            }}
          >
            {header}
          </div>
        )}
        <div style={{ flex: 1 }}>{children}</div>
        {footer && (
          <div
            style={{
              marginTop: theme.spacing.md,
              paddingTop: theme.spacing.sm,
              borderTop: `1px solid ${theme.colors.gray[100]}`,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    );
  },
);

Card.displayName = "Card";

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-base font-bold leading-none text-foreground tracking-tight",
      className,
    )}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";

export { CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
